import { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faTimes, faArrowLeft, faChartLine } from '@fortawesome/free-solid-svg-icons';
import UnitMappingService from '../services/unitMappingService';
import { useTranslate } from '../translations/useTranslate';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Dot } from 'recharts';
import './xyz.css';
import { StandardSearch, highlightMatch } from '../styles/standardStyles';

/**
 * XYZ Component - Clean, Modern ABC Analysis Implementation
 * This is a completely rewritten version with better architecture and responsiveness
 */
export default function Xyz({ goTo, previousScreen }) {
  // ========== CONFIGURATION ==========
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const { language } = useTranslate();
  
  // ========== STATE MANAGEMENT ==========
  const [view, setView] = useState("list"); // "list" or "details"
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState("A");
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [itemToDemote, setItemToDemote] = useState(null);
  const [items, setItems] = useState({ A: [], B: [], C: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editable thresholds state
  const [editingROP, setEditingROP] = useState(false);
  const [editingSafety, setEditingSafety] = useState(false);
  const [ropValue, setRopValue] = useState('');
  const [safetyValue, setSafetyValue] = useState('');
  const [savingThresholds, setSavingThresholds] = useState(false);
  
  // Monthly cost chart data
  const [monthlyCostData, setMonthlyCostData] = useState([]);
  const [monthlyCostLoading, setMonthlyCostLoading] = useState(false);

  // Refs for managing fetch and data
  const searchInputRef = useRef(null);
  const lastGoodDataRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const dataSignatureRef = useRef(null);
  const sseConnectionRef = useRef(null);
  const mountedRef = useRef(true); // §4.12: guard setState after unmount

  // Track mount state so async fetches never setState on an unmounted component.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ========== UTILITY FUNCTIONS ==========
  
  /**
   * Format quantity with appropriate units and auto-conversion
   * < 1000 = g/ml, >= 1000 = kg/L
   */
  const formatQuantity = useCallback((quantity, unitSymbol = 'units') => {
    if (!quantity || quantity === 0) return '0 units';
    
    const numQuantity = parseFloat(quantity);
    const isNegative = numQuantity < 0;
    const absQuantity = Math.abs(numQuantity);
    const unit = (unitSymbol || 'units').toLowerCase().trim();
    const displayUnit = UnitMappingService.normalizeUnitSymbol(unitSymbol || 'units');

    // Auto-convert units when absolute value >= 1000
    // Grams to Kilograms
    if ((unit === 'g' || unit === 'gram' || unit === 'grams') && absQuantity >= 1000) {
      const kgValue = absQuantity / 1000;
      const formatted = kgValue >= 10 
        ? `${Math.round(kgValue)} kg`
        : `${(Math.round(kgValue * 10) / 10).toFixed(1)} kg`;
      return isNegative ? `-${formatted}` : formatted;
    }
    
    // Milliliters to Liters
    if ((unit === 'ml' || unit === 'milliliter' || unit === 'milliliters') && absQuantity >= 1000) {
      const literValue = absQuantity / 1000;
      const formatted = literValue >= 10 
        ? `${Math.round(literValue)} L`
        : `${(Math.round(literValue * 10) / 10).toFixed(1)} L`;
      return isNegative ? `-${formatted}` : formatted;
    }
    
    // Milligrams to Grams
    if ((unit === 'mg' || unit === 'milligram' || unit === 'milligrams') && absQuantity >= 1000) {
      const gValue = absQuantity / 1000;
      const formatted = gValue >= 10 
        ? `${Math.round(gValue)} g`
        : `${(Math.round(gValue * 10) / 10).toFixed(1)} g`;
      return isNegative ? `-${formatted}` : formatted;
    }

    // Smart formatting based on quantity and unit type (< 1000 threshold)
    if (unit === 'kg') {
      const grams = absQuantity * 1000;
      if (grams < 1000) {
        const formatted = `${grams.toFixed(0)} g`;
        return isNegative ? `-${formatted}` : formatted;
      }
    }
    
    if (unit === 'l') {
      const ml = absQuantity * 1000;
      if (ml < 1000) {
        const formatted = `${ml.toFixed(0)} ml`;
        return isNegative ? `-${formatted}` : formatted;
      }
    }
    
    // Default formatting — use normalized display unit
    if (absQuantity < 1) {
      const formatted = `${absQuantity.toFixed(2)} ${displayUnit}`;
      return isNegative ? `-${formatted}` : formatted;
    } else if (absQuantity < 10) {
      const formatted = `${absQuantity.toFixed(2)} ${displayUnit}`;
      return isNegative ? `-${formatted}` : formatted;
    } else {
      const formatted = `${absQuantity.toFixed(0)} ${displayUnit}`;
      return isNegative ? `-${formatted}` : formatted;
    }
  }, []);

  /**
   * Format currency in INR
   */
  const formatCurrency = useCallback((value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(num);
  }, []);

  /**
   * Get the "top family" unit for price display.
   * Items stored in g/mg → show price per kg.
   * Items stored in ml → show price per L.
   * All others → return as-is.
   */
  const getPriceDisplayUnit = useCallback((unitSymbol) => {
    const u = (unitSymbol || '').toLowerCase().trim();
    if (u === 'g' || u === 'gram' || u === 'grams' || u === 'mg' || u === 'milligram' || u === 'milligrams') return 'kg';
    if (u === 'ml' || u === 'milliliter' || u === 'milliliters') return 'L';
    return unitSymbol || 'unit';
  }, []);

  /**
   * Convert a raw unit_cost (per item's stored unit) to the top-family price.
   * e.g. ₹3/g → ₹3000/kg, ₹5/ml → ₹5000/L
   * Returns { displayPrice, priceUnit }
   */
  const formatUnitPrice = useCallback((rawCost, unitSymbol) => {
    const cost = parseFloat(rawCost || 0);
    const u = (unitSymbol || '').toLowerCase().trim();
    let displayPrice = cost;
    let priceUnit = unitSymbol || 'unit';

    if (u === 'g' || u === 'gram' || u === 'grams') {
      displayPrice = cost * 1000;
      priceUnit = 'kg';
    } else if (u === 'mg' || u === 'milligram' || u === 'milligrams') {
      displayPrice = cost * 1000;
      priceUnit = 'g';
    } else if (u === 'ml' || u === 'milliliter' || u === 'milliliters') {
      displayPrice = cost * 1000;
      priceUnit = 'L';
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(displayPrice);

    return { displayPrice, priceUnit, formatted };
  }, []);

  // NOTE: the old `computeValueForQuantity` (with its `cost >= 50` magnitude guess,
  // Bug 10) has been deleted. All monetary values now come pre-computed from the
  // backend, from matched (base qty × cost-per-base-unit) triples (Bugs 9, 10).

  /**
   * Build signature to detect real data changes
   */
  const buildDataSignature = useCallback((dataByCat) => {
    try {
      return ['A', 'B', 'C'].map(cat =>
        (dataByCat[cat] || [])
          .map(i => `${i.id}|${i.qty}|${i.value}|${i.stockStatus}`)
          .join(',')
      ).join('||');
    } catch {
      return Math.random().toString(36);
    }
  }, []);

  /**
   * Smart search function - search across multiple fields
   */
  const searchItems = useCallback((itemList, term) => {
    if (!term.trim()) return itemList;
    
    const searchLower = term.toLowerCase().trim();
    return itemList.filter(item => {
      const itemName = (item.name || '').toLowerCase().trim();
      const itemSupplier = (item.supplier || '').toLowerCase().trim();
      const itemType = (item.type || '').toLowerCase().trim();
      const itemCategory = (item.category || '').toLowerCase().trim();
      
      return itemName.includes(searchLower) ||
             itemSupplier.includes(searchLower) ||
             itemType.includes(searchLower) ||
             itemCategory.includes(searchLower);
    });
  }, []);

  /**
   * Highlight matching text in search results
   */
  const highlightText = useCallback((text, term) => {
    if (!term.trim() || !text) return text;
    
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="xyz-highlight">{part}</mark>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  }, []);

  // ========== DATA FETCHING & PROCESSING ==========
  //
  // NOTE: there is NO client-side ABC classifier. All A/B/C classification is
  // done by the ONE backend engine (services/abcCore.js). A previous
  // `classifyLocally` fallback read the wrong field names (avg_unit_cost /
  // is_manual_override) and silently dumped every item into C (Bug 7). It has
  // been removed; if the backend ever returns items without categories that is
  // now surfaced as an explicit error instead of a wrong all-C list.

  /**
   * Transform raw backend list data to UI format.
   *
   * RESPONSE CONTRACT (Bug 11) — the LIST endpoints
   * (`/weekly-audit/abc-integration` and `/xyz-analysis/calculate`) return only:
   *   item_id, item_name/name, abc_category, consumption_value, current_stock,
   *   unit_symbol, primary_supplier, reorder_point, safety_stock, stock_status,
   *   percentage_of_total (calculate only), is_manually_promoted.
   * Per-item monthly usage / waste / latest price / expiry are NOT in the list —
   * they are fetched on demand by `item-details` when a card is opened. We no
   * longer fabricate zero-valued keys for them here (that implied data that the
   * list never has). Fields the DETAILS view needs as a fallback are kept.
   */
  const transformItemData = useCallback((item, category) => {
    return {
      id: Number(item.item_id ?? item.itemId ?? item.id ?? 0),
      name: item.item_name || item.itemName || item.name || 'Unknown Item',
      category: category,
      type: 'Inventory Item',
      qty: formatQuantity(item.current_stock ?? item.total_quantity_used, item.unit_symbol),
      supplier: item.primary_supplier || 'Various',
      value: formatCurrency(item.inventory_value ?? item.consumption_value ?? 0),
      usagePercent: Math.round(parseFloat(item.percentage_of_total || 0)),
      consumptionValue: parseFloat(item.consumption_value || 0),
      quantityUsed: parseFloat(item.total_quantity_used || 0),
      unitSymbol: item.unit_symbol || 'units',
      currentStock: parseFloat(item.current_stock ?? 0),
      reorderPoint: parseFloat(item.reorder_point ?? 0),
      safetyStock: parseFloat(item.safety_stock ?? 0),
      stockStatus: item.stock_status || 'sufficient',
      isManuallyPromoted: item.is_manually_promoted === true
    };
  }, [formatQuantity, formatCurrency]);

  /**
   * Fetch and process ABC analysis data
   */
  const fetchData = useCallback(async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
        setError(null);
      }

      // Cancel any in-flight request
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      fetchAbortRef.current = controller;

      // Get dynamic business ID from logged-in user
      const businessId = UnitMappingService.getBusinessId();
      if (!businessId) {
        throw new Error('Unable to determine business context');
      }
      const scopedCacheKey = `xyz-cache-${businessId}`;

      let categorizedData;

      // 1) Prefer DB-stored A/B/C categories so tabs match ABCAnalysisResults exactly.
      const storedResponse = await fetch(
        `${API_BASE_URL}/weekly-audit/abc-integration?businessId=${businessId}&language=${language}`,
        {
          signal: controller.signal,
          cache: 'no-store'
        }
      );

      if (storedResponse.ok) {
        const storedData = await storedResponse.json();
        const categories = storedData?.abcCategories;
        if (categories && typeof categories === 'object') {
          categorizedData = {
            A: (categories.A || []).map((item) => transformItemData(item, 'A')),
            B: (categories.B || []).map((item) => transformItemData(item, 'B')),
            C: (categories.C || []).map((item) => transformItemData(item, 'C')),
          };
        }
      }

      // 2) Fallback to dynamic xyz calculation only if stored categories are unavailable.
      if (!categorizedData) {
        const response = await fetch(
          `${API_BASE_URL}/xyz-analysis/calculate?businessId=${businessId}&language=${language}`,
          {
            signal: controller.signal,
            cache: 'no-store'
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch data`);
        }

        const data = await response.json();
        if (!data.success || !data.data) {
          throw new Error('Invalid response format from API');
        }

        if (data.data.items_by_category) {
          categorizedData = {
            A: data.data.items_by_category.A.map(item => transformItemData(item, 'A')),
            B: data.data.items_by_category.B.map(item => transformItemData(item, 'B')),
            C: data.data.items_by_category.C.map(item => transformItemData(item, 'C'))
          };
        } else if (data.data.items) {
          const analysisResults = data.data.items;
          const hasBackendCategories = analysisResults.some(
            item => ['A', 'B', 'C'].includes(item.abc_category)
          );

          if (hasBackendCategories) {
            categorizedData = { A: [], B: [], C: [] };
            analysisResults.forEach(item => {
              const category = ['A', 'B', 'C'].includes(item.abc_category)
                ? item.abc_category
                : 'C';
              categorizedData[category].push(transformItemData(item, category));
            });
          } else {
            // Backend returned items without categories → contract violation.
            // Do NOT re-classify on the client (that caused the all-C Bug 7).
            // Surface it; the catch below falls back to last-good data + error.
            throw new Error('Backend returned items without ABC categories');
          }
        }
      }

      if (!categorizedData) {
        throw new Error('No categorized items data received from backend');
      }

      // Keep item order stable and predictable within each category.
      Object.keys(categorizedData).forEach(cat => {
        categorizedData[cat].sort((a, b) => {
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        });
      });

      // Check if data actually changed
      const newSignature = buildDataSignature(categorizedData);
      if (!mountedRef.current) return; // §4.12
      if (newSignature !== dataSignatureRef.current) {
        setItems(categorizedData);
        lastGoodDataRef.current = categorizedData;
        dataSignatureRef.current = newSignature;

        // Cache data
        try {
          localStorage.setItem(scopedCacheKey, JSON.stringify({
            data: categorizedData,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to cache data:', e);
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') return;
      if (!mountedRef.current) return; // §4.12

      console.error('Error fetching ABC data:', error);
      setError(error.message);

      // Use cached data if available
      if (lastGoodDataRef.current) {
        setItems(lastGoodDataRef.current);
      }
    } finally {
      if (!isBackgroundRefresh && mountedRef.current) {
        setLoading(false);
      }
    }
  }, [transformItemData, buildDataSignature, API_BASE_URL, language]);

  /**
   * Load cached data for instant display
   */
  const loadCache = useCallback(() => {
    try {
      const businessId = UnitMappingService.getBusinessId();
      const scopedCacheKey = `xyz-cache-${businessId || 'unknown'}`;
      // Remove legacy global cache to avoid category leakage across businesses.
      if (localStorage.getItem('xyz-cache')) {
        localStorage.removeItem('xyz-cache');
      }

      const cached = localStorage.getItem(scopedCacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        // Use cache if less than 5 minutes old
        if (age < 5 * 60 * 1000 && data) {
          setItems(data);
          lastGoodDataRef.current = data;
          dataSignatureRef.current = buildDataSignature(data);
          setLoading(false);
          return true;
        }
      }
    } catch (e) {
      console.warn('Failed to load cache:', e);
    }
    return false;
  }, [buildDataSignature]);

  // ========== EFFECTS ==========

  /**
   * Initial data load and setup
   */
  useEffect(() => {
    let debounceTimer;

    // Read preferred defaults set by Dashboard alerts
    try {
      const defTab = localStorage.getItem('xyz_default_tab');
      if (defTab && ['A','B','C'].includes(defTab)) {
        setActiveTab(defTab);
        // Clear it after reading so it doesn't persist
        localStorage.removeItem('xyz_default_tab');
      }
      
      const focusItem = localStorage.getItem('xyz_focus_item');
      if (focusItem) {
        // Store it temporarily to use after data loads
        sessionStorage.setItem('xyz_scroll_to', focusItem);
        localStorage.removeItem('xyz_focus_item');
      }
    } catch {}
    const hasCachedData = loadCache();
    fetchData(!hasCachedData);

    // Background refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchData(true);
    }, 30000);

    // Setup SSE for real-time updates, with automatic backoff reconnect (Bug 16).
    // §4.12: only open the stream when we actually have a business id; otherwise
    // rely on the 30s polling backstop instead of connecting to `?businessId=null`.
    const businessId = UnitMappingService.getBusinessId();
    let reconnectTimer;
    let backoff = 1000;
    let closedByCleanup = false;

    const handleUpdate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => fetchData(true), 600);
    };

    const connectSSE = () => {
      if (!businessId) return; // no valid business → skip SSE, polling covers it
      try {
        const es = new EventSource(`${API_BASE_URL}/xyz-analysis/stream?businessId=${businessId}`);
        es.addEventListener('xyz.invalidate', handleUpdate);
        es.onopen = () => { backoff = 1000; };
        es.onerror = () => {
          es.close();
          if (closedByCleanup) return;
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connectSSE, backoff);
          backoff = Math.min(backoff * 2, 10000); // cap at 10s
        };
        sseConnectionRef.current = es;
      } catch (e) {
        console.warn('SSE connection failed:', e);
      }
    };
    connectSSE();

    return () => {
      closedByCleanup = true;
      clearInterval(refreshInterval);
      clearTimeout(debounceTimer);
      clearTimeout(reconnectTimer);
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      if (sseConnectionRef.current) {
        sseConnectionRef.current.close();
      }
    };
  }, [fetchData, loadCache, language]); // Added language dependency

  /**
   * Scroll to and highlight focus item from Dashboard alert
   */
  useEffect(() => {
    if (loading || !items || Object.keys(items).length === 0) return;
    
    try {
      const scrollToItem = sessionStorage.getItem('xyz_scroll_to');
      if (!scrollToItem) return;
      
      // Find the item in the current tab
      const currentItems = items[activeTab] || [];
      const targetItem = currentItems.find(item => 
        item.name && item.name.toLowerCase() === scrollToItem.toLowerCase()
      );
      
      if (targetItem) {
        // Wait a bit for DOM to render
        setTimeout(() => {
          // Try to find the element by data attribute or text content
          const itemElements = document.querySelectorAll('.xyz-item-card, .xyz-list-item');
          for (const el of itemElements) {
            if (el.textContent.includes(targetItem.name)) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              // Add highlight effect
              el.style.transition = 'background-color 0.3s';
              el.style.backgroundColor = '#fff3cd';
              setTimeout(() => {
                el.style.backgroundColor = '';
              }, 2000);
              break;
            }
          }
        }, 300);
        
        // Clear the session storage after using it
        sessionStorage.removeItem('xyz_scroll_to');
      }
    } catch (e) {
      console.warn('Failed to scroll to focus item:', e);
    }
  }, [items, activeTab, loading]);

  // ========== EVENT HANDLERS ==========

  const handleBack = () => {
    if (view === "details") {
      setView("list");
      setSelectedItem(null);
      // Reset editing states
      setEditingROP(false);
      setEditingSafety(false);
    } else if (typeof goTo === 'function') {
      goTo('create-reorder');
    } else {
      window.history.back();
    }
  };

  /**
   * Handle saving ROP (Reorder Point)
   */
  // §4.12: reject empty, non-numeric, scientific-notation, negative, and absurd values.
  const parseThreshold = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s || /[eE]/.test(s)) return null;          // reject '', '1e9', 'abc'
    const n = Number(s);
    if (!Number.isFinite(n) || n < 0 || n > 1e7) return null;
    return n;
  };

  const handleSaveROP = async () => {
    if (!selectedItem) return;
    const rop = parseThreshold(ropValue);
    if (rop === null) {
      alert('Enter a valid reorder point (0 – 10,000,000).');
      return;
    }

    setSavingThresholds(true);
    try {
      const businessId = UnitMappingService.getBusinessId();
      const response = await fetch(`${API_BASE_URL}/xyz-analysis/items/${selectedItem.id}/thresholds`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          reorder_point: rop
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setSelectedItem(prev => ({
          ...prev,
          reorderPoint: rop
        }));
        setEditingROP(false);
        
        // Refresh the list
        fetchData(true);
      } else {
        alert(data.error || 'Failed To Update ROP');
      }
    } catch (error) {
      console.error('Error saving ROP:', error);
      alert('Failed To Update ROP');
    } finally {
      setSavingThresholds(false);
    }
  };

  /**
   * Handle saving Safety Stock
   */
  const handleSaveSafety = async () => {
    if (!selectedItem) return;
    const safety = parseThreshold(safetyValue);
    if (safety === null) {
      alert('Enter a valid safety stock (0 – 10,000,000).');
      return;
    }

    setSavingThresholds(true);
    try {
      const businessId = UnitMappingService.getBusinessId();
      const response = await fetch(`${API_BASE_URL}/xyz-analysis/items/${selectedItem.id}/thresholds`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessId,
          safety_stock: safety
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setSelectedItem(prev => ({
          ...prev,
          safetyStock: safety
        }));
        setEditingSafety(false);
        
        // Refresh the list
        fetchData(true);
      } else {
        alert(data.error || 'Failed To Update Safety Stock');
      }
    } catch (error) {
      console.error('Error saving Safety Stock:', error);
      alert('Failed To Update Safety Stock');
    } finally {
      setSavingThresholds(false);
    }
  };

  /**
   * Handle clicking edit for ROP
   */
  const handleEditROP = () => {
    setRopValue(selectedItem.reorderPoint?.toString() || '');
    setEditingROP(true);
  };

  /**
   * Handle clicking edit for Safety Stock
   */
  const handleEditSafety = () => {
    setSafetyValue(selectedItem.safetyStock?.toString() || '');
    setEditingSafety(true);
  };

  /**
   * Cancel editing ROP
   */
  const handleCancelROP = () => {
    setEditingROP(false);
    setRopValue('');
  };

  /**
   * Cancel editing Safety Stock
   */
  const handleCancelSafety = () => {
    setEditingSafety(false);
    setSafetyValue('');
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleItemClick = async (item) => {
    try {
      // Get dynamic business ID
      const businessId = UnitMappingService.getBusinessId();

      // Fetch real-time item details from backend
      const response = await fetch(
        `${API_BASE_URL}/xyz-analysis/item-details/${item.id}?businessId=${businessId}`,
        { cache: 'no-store' }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch item details`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('Invalid response format from API');
      }

      const details = result.data;

      // Transform backend data to UI format with real-time values
      const resolvedItemId = Number(
        details.item_id
        ?? details.itemId
        ?? item.item_id
        ?? item.itemId
        ?? item.id
        ?? 0
      );
      // Unit Contract: usage/waste are in BASE units (g/ml) → label with usage_display_unit.
      // current_stock is in the item's own unit → label with stock_unit.
      // All ₹ values come pre-computed from the backend (matched qty×cost×unit).
      const usageDisplayUnit = details.usage_display_unit || details.unit_symbol || 'units';
      const stockUnit = details.stock_unit || details.unit_symbol || 'units';
      const inventoryValue = parseFloat(details.inventory_value || 0);
      const usageValue = parseFloat(details.usage_value || 0);
      const wasteValue = parseFloat(details.waste_value || 0);

      const enrichedItem = {
        ...item,
        id: Number.isFinite(resolvedItemId) && resolvedItemId > 0 ? resolvedItemId : item.id,
        // Stock information from InventoryBatches (item's own unit)
        currentStock: parseFloat(details.current_stock || 0),
        qty: formatQuantity(details.current_stock || 0, stockUnit),
        unitSymbol: stockUnit,

        // Inventory value (backend: stock × unit_cost, both in item's own unit)
        inventory_value: inventoryValue,
        value: formatCurrency(inventoryValue),

        // Bug 2 fix: preserve the original value contribution percentage from the list view
        // usage_percentage is a different metric (this_month_usage / total_usage)
        valueContributionPercent: item.usagePercent, // preserve original list-view % of total value
        usagePercent: Math.round(parseFloat(details.usage_percentage || 0)),
        monthlyUsageQty: formatQuantity(details.this_month_usage || 0, usageDisplayUnit),
        monthlyUsageValue: formatCurrency(usageValue),
        totalUsage: parseFloat(details.total_usage || 0),

        // Wastage information
        wasteMonthQty: formatQuantity(details.total_waste || 0, usageDisplayUnit),
        wasteMonthValue: formatCurrency(wasteValue),
        totalWaste: parseFloat(details.total_waste || 0),
        wasteValue: wasteValue,

        // Stock status and supplier info
        stockStatus: details.stock_alert || 'sufficient',
        supplier: details.supplier_name || item.supplier || 'Various',
        latestUnitCost: parseFloat(details.unit_cost || 0),
        reorderPoint: parseFloat(details.reorder_point || 0),

        // Expiry information
        expiryDate: details.expiry_date || null,
        receivedDate: details.received_date || null,
        daysUntilExpiry: details.days_until_expiry || null
      };

      setSelectedItem(enrichedItem);
      setView("details");

      // Fetch monthly cost data for bar chart
      setMonthlyCostLoading(true);
      try {
        const costResponse = await fetch(
          `${API_BASE_URL}/xyz-analysis/item-monthly-cost/${item.id}?businessId=${businessId}`,
          { cache: 'no-store' }
        );
        if (costResponse.ok) {
          const costResult = await costResponse.json();
          if (costResult.success && costResult.data) {
            setMonthlyCostData(costResult.data);
          }
        }
      } catch (costErr) {
        console.error('Error fetching monthly cost data:', costErr);
      } finally {
        setMonthlyCostLoading(false);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      alert(`❌ Error loading item details: ${error.message}`);
    }
  };

  const handleMoveToA = (item) => {
    if (!item.id) {
      alert('Cannot move this item - invalid ID');
      return;
    }
    setItemToMove(item);
    setShowMoveModal(true);
  };

  const handleDemoteToB = (item) => {
    if (!item.id) {
      alert('Cannot move this item - invalid ID');
      return;
    }
    setItemToDemote(item);
    setShowDemoteModal(true);
  };

  // Bug 14: optimistic move that does NOT assume the source category. Removes the
  // item wherever it currently is (A/B/C) and prepends it to the target category.
  const optimisticMove = (id, item, target) => setItems(prev => {
    const next = { A: [...prev.A], B: [...prev.B], C: [...prev.C] };
    for (const c of ['A', 'B', 'C']) next[c] = next[c].filter(i => i.id !== id);
    next[target] = [{ ...item, category: target }, ...next[target]];
    return next;
  });

  const confirmMoveToA = async () => {
    if (!itemToMove) return;

    try {
      // Get dynamic business ID
      const businessId = UnitMappingService.getBusinessId();

      if (!businessId) {
        alert('❌ Error: Unable to determine business ID. Please refresh and try again.');
        return;
      }


      // Optimistic UI update (source-agnostic — Bug 14)
      optimisticMove(itemToMove.id, itemToMove, 'A');

      // API call to promote item
      const response = await fetch(`${API_BASE_URL}/xyz-analysis/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: Number(itemToMove.id),
          businessId: Number(businessId)
        })
      });

      const result = await response.json();
      

      if (!response.ok) {
        throw new Error(result.error || result.details || `Server error: ${response.status}`);
      }

      if (result.success) {
        // Clear cache and refresh
        localStorage.removeItem(`xyz-cache-${businessId}`);
        localStorage.removeItem('xyz-cache');
        await fetchData(false);
        setActiveTab('A'); // Switch to A tab
        alert(`✅ ${itemToMove.name} moved to Category A`);
      } else {
        throw new Error(result.error || result.message || 'Failed to move item');
      }

    } catch (error) {
      console.error('❌ Error moving item:', error);
      alert(`❌ Error: ${error.message}\n\nPlease check:\n1. Internet connection\n2. Server is running\n3. Item exists in database`);
      fetchData(false); // Revert by refetching
    } finally {
      setShowMoveModal(false);
      setItemToMove(null);
    }
  };

  const confirmDemoteToB = async () => {
    if (!itemToDemote) return;

    try {
      // Get dynamic business ID
      const businessId = UnitMappingService.getBusinessId();

      if (!businessId) {
        alert('❌ Error: Unable to determine business ID. Please refresh and try again.');
        return;
      }


      // Optimistic UI update (source-agnostic — Bug 14)
      optimisticMove(itemToDemote.id, { ...itemToDemote, isManuallyPromoted: false }, 'B');

      // API call to demote item
      const response = await fetch(`${API_BASE_URL}/xyz-analysis/demote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemId: Number(itemToDemote.id),
          businessId: Number(businessId)
        })
      });

      const result = await response.json();
      

      if (!response.ok) {
        throw new Error(result.error || result.details || `Server error: ${response.status}`);
      }

      if (result.success) {
        // Clear cache and refresh
        localStorage.removeItem(`xyz-cache-${businessId}`);
        localStorage.removeItem('xyz-cache');
        await fetchData(false);
        setActiveTab('B'); // Switch to B tab
        alert(`✅ ${itemToDemote.name} moved back to Category B`);
      } else {
        throw new Error(result.error || result.message || 'Failed to move item');
      }

    } catch (error) {
      console.error('❌ Error moving item:', error);
      alert(`❌ Error: ${error.message}\n\nPlease check:\n1. Internet connection\n2. Server is running\n3. Item exists in database`);
      fetchData(false); // Revert by refetching
    } finally {
      setShowDemoteModal(false);
      setItemToDemote(null);
    }
  };

  // ========== FILTERED DATA ==========
  const filteredItems = {
    A: searchItems(items.A, searchTerm),
    B: searchItems(items.B, searchTerm),
    C: searchItems(items.C, searchTerm)
  };

  // ========== RENDER VIEWS ==========

  /**
   * Error Screen
   */
  if (error && !lastGoodDataRef.current) {
    return (
      <div className="xyz-container">
        <div className="xyz-error-screen">
          <h3>⚠️ Unable to Load Data</h3>
          <p>{error}</p>
          <button className="xyz-btn xyz-btn-primary" onClick={() => fetchData()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  /**
   * List View
   */
  if (view === "list") {
    return (
      <div className="xyz-container">
        {/* Header */}
        <div className="xyz-header">
          {/* Single back button logic: hide when from dashboard; else normal back */}
          {previousScreen !== 'dashboard' && (
            <button
              className="xyz-back-btn"
              onClick={handleBack}
              aria-label="Back"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          <div className="xyz-header-content">
        
            <h1>ABC Analysis</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="xyz-tabs">
          {["A", "B", "C"].map((tab) => (
            <button
              key={tab}
              className={`xyz-tab ${activeTab === tab ? 'xyz-tab-active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}-Items
            </button>
          ))}
        </div>

        {/* Standard Search Bar (replaces custom implementation) */}
        <StandardSearch
          value={searchTerm}
          onChange={(val) => handleSearch(val)}
          onClear={clearSearch}
          placeholder="Search Items"
          inputRef={searchInputRef}
        />

        {/* Items List */}
        <div className="xyz-items-list">
          {filteredItems[activeTab].length === 0 ? (
            <div className="xyz-empty-state">
              
              <h3>
                {searchTerm 
                  ? `No ${activeTab}-category items match "${searchTerm}"`
                  : `No ${activeTab}-category items found`
                }
              </h3>
              <p>
                {!searchTerm && activeTab === 'A' && "High-value items will appear here"}
                
                {!searchTerm && activeTab === 'C' && "Low-value items will appear here"}
                {searchTerm && "Try adjusting your search terms"}
              </p>
            </div>
          ) : (
            filteredItems[activeTab].map((item) => (
              <div
                key={item.id}
                className="xyz-item-card xyz-item-clickable"
                onClick={() => handleItemClick(item)}
              >
                <div className="xyz-item-info">
                  <h3 className="xyz-item-name">
                    {highlightText(item.name, searchTerm)}
                  </h3>
                  <div className="xyz-item-qty">{item.qty}</div>
                </div>
                <div className="xyz-item-meta">
                  {activeTab === "A" && (
                    item.isManuallyPromoted ? (
                      <button
                        className="xyz-manage-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDemoteToB(item);
                        }}
                      >
                        Tap to manage
                      </button>
                    ) : (
                      <span className="xyz-badge xyz-badge-high">High-Value</span>
                    )
                  )}
                  {activeTab === "B" && (
                    <button
                      className="xyz-manage-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveToA(item);
                      }}
                    >
                      Tap to manage
                    </button>
                  )}
                  {activeTab === "C" && (
                    <span className="xyz-badge xyz-badge-basic">Basic Item</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Move to A Modal */}
        {showMoveModal && (
          <div className="xyz-modal-overlay" onClick={() => setShowMoveModal(false)}>
            <div className="xyz-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Move to Category A?</h3>
              <p>Move "{itemToMove?.name}" to high-priority category?</p>
              <div className="xyz-modal-actions">
                <button className="xyz-btn xyz-btn-secondary" onClick={() => setShowMoveModal(false)}>
                  Cancel
                </button>
                <button className="xyz-btn xyz-btn-primary" onClick={confirmMoveToA}>
                  Move
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Demote to B Modal */}
        {showDemoteModal && (
          <div className="xyz-modal-overlay" onClick={() => setShowDemoteModal(false)}>
            <div className="xyz-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Move back to Category B?</h3>
              <p>Move "{itemToDemote?.name}" back to Category B? This will remove the manual promotion.</p>
              <div className="xyz-modal-actions">
                <button className="xyz-btn xyz-btn-secondary" onClick={() => setShowDemoteModal(false)}>
                  Cancel
                </button>
                <button className="xyz-btn xyz-btn-primary" onClick={confirmDemoteToB}>
                  Move
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * Details View (opens for any category on card click — Bug 17: comment fixed)
   */
  if (view === "details" && selectedItem) {
    return (
      <div className="xyz-container">
        {/* Details Header */}
        <div className="xyz-details-header">
          <button
            className="xyz-back-btn"
            onClick={handleBack}
            aria-label="Back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h2 style={{textAlign:'left',paddingLeft:'0.2rem'}}>Item Details</h2>
        </div>

        {/* Main Info Card */}
        <div className="xyz-details-card">
          <div className="xyz-details-main-header">
            <h3 className="xyz-details-title">
              <span className="xyz-details-name">{selectedItem.name}</span> <span className="xyz-details-qty">{selectedItem.qty}</span>
            </h3>
            {selectedItem.latestUnitCost > 0 && (() => {
              const { formatted, priceUnit } = formatUnitPrice(selectedItem.latestUnitCost, selectedItem.unitSymbol);
              const rawUnit = (selectedItem.unitSymbol || '').toLowerCase().trim();
              const needsConversion = ['g','gram','grams','mg','milligram','milligrams','ml','milliliter','milliliters'].includes(rawUnit);
              return (
                <p className="xyz-details-subtitle">
                  <strong>Latest Price: {formatted}/{priceUnit}</strong>
                  {needsConversion && (
                    <span className="xyz-details-conversion">
                      (₹{Number(selectedItem.latestUnitCost).toFixed(4)}/{selectedItem.unitSymbol})
                    </span>
                  )}
                </p>
              );
            })()}
          </div>
          <div className="xyz-details-info">
            <div className="xyz-info-row">
              <span>Primary Supplier</span>
              <strong>{selectedItem.supplier}</strong>
            </div>
            <div className="xyz-info-row">
              <span>Expiry Date</span>
              <strong className="xyz-expiry">
                {selectedItem.expiryDate 
                  ? (() => {
                      const date = new Date(selectedItem.expiryDate);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()
                  : 'N/A'
                }
              </strong>
            </div>
          </div>
        </div>

        {/* ROP and Safety Stock Editable Cards */}
        <div className="xyz-threshold-section">
          <h3 className="xyz-threshold-title">Stock Management Thresholds</h3>
          <div className="xyz-threshold-grid">
            {/* Safety Stock Card moved to first position (replaces Current Stock) */}
            <div className="xyz-threshold-card">
              <div className="xyz-threshold-header">
                <h4>Safety Stock</h4>
                {!editingSafety && (
                  <button 
                    className="xyz-edit-btn" 
                    onClick={handleEditSafety}
                    disabled={savingThresholds}
                    aria-label="Edit"
                  >
                    <svg width="1.3rem" height="1.3rem" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              {editingSafety ? (
                <div className="xyz-threshold-edit">
                  <div className="xyz-input-group">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={safetyValue}
                      onChange={(e) => setSafetyValue(e.target.value)}
                      placeholder="Enter Safety Stock Value"
                      className="xyz-threshold-input"
                      disabled={savingThresholds}
                      autoFocus
                    />
                    <span className="xyz-input-unit">{selectedItem.unitSymbol.toLowerCase()}</span>
                  </div>
                  <div className="xyz-threshold-actions">
                    <button 
                      className="xyz-btn-cancel" 
                      onClick={handleCancelSafety}
                      disabled={savingThresholds}
                    >
                      Cancel
                    </button>
                    <button 
                      className="xyz-btn-save" 
                      onClick={handleSaveSafety}
                      disabled={savingThresholds || !safetyValue.trim()}
                    >
                      {savingThresholds ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="xyz-threshold-display">
                  <div className="xyz-threshold-value">
                    {selectedItem.safetyStock ? 
                      formatQuantity(selectedItem.safetyStock, selectedItem.unitSymbol) : 
                      'Not set'}
                  </div>
                  <div className="xyz-threshold-description">
                    Buffer stock to prevent stockouts
                  </div>
                </div>
              )}
            </div>

            {/* Reorder Point Card */}
            <div className="xyz-threshold-card">
              <div className="xyz-threshold-header">
                <h4>ROP (Reorder Point)</h4>
                {!editingROP && (
                  <button 
                    className="xyz-edit-btn" 
                    onClick={handleEditROP}
                    disabled={savingThresholds}
                    aria-label="Edit"
                  >
                    <svg width="1.3rem" height="1.3rem" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                )}
              </div>
              {editingROP ? (
                <div className="xyz-threshold-edit">
                  <div className="xyz-input-group">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ropValue}
                      onChange={(e) => setRopValue(e.target.value)}
                      placeholder="Enter ROP Value"
                      className="xyz-threshold-input"
                      disabled={savingThresholds}
                      autoFocus
                    />
                    <span className="xyz-input-unit">{selectedItem.unitSymbol.toLowerCase()}</span>
                  </div>
                  <div className="xyz-threshold-actions">
                    <button 
                      className="xyz-btn-cancel" 
                      onClick={handleCancelROP}
                      disabled={savingThresholds}
                    >
                      Cancel
                    </button>
                    <button 
                      className="xyz-btn-save" 
                      onClick={handleSaveROP}
                      disabled={savingThresholds || !ropValue.trim()}
                    >
                      {savingThresholds ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="xyz-threshold-display">
                  <div className="xyz-threshold-value">
                    {selectedItem.reorderPoint ? 
                      formatQuantity(selectedItem.reorderPoint, selectedItem.unitSymbol) : 
                      'Not set'}
                  </div>
                  <div className="xyz-threshold-description">
                    Minimum stock level before reordering
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Stock & Value Grid */}
        <div className="xyz-details-grid">
          <div className="xyz-metric-card">
            <div className="xyz-metric-header">
              <h4>Current Stock</h4>
            </div>
            <div className="xyz-metric-value" style={{color:'#000'}}>
              {formatQuantity(selectedItem.currentStock, selectedItem.unitSymbol)}
            </div>
            <div className="xyz-metric-label">Available Inventory Level</div>
          </div>

          <div className="xyz-metric-card">
            <div className="xyz-metric-header">
              <h4>Total Inventory Value</h4>
            </div>
            <div className="xyz-metric-value" style={{color:'#000'}}>{selectedItem.value}</div>
            <div className="xyz-metric-label">Inventory Value</div>
          </div>
        </div>

        {/* Usage & Waste Grid */}
        <div className="xyz-details-grid">
          <div className="xyz-metric-card">
            <div className="xyz-metric-header">
              <h4>Usage</h4>
            </div>
            <div className="xyz-metric-value" style={{color:'#000'}}>
              {selectedItem.monthlyUsageValue || '₹0'}
            </div>
            <div className="xyz-metric-label">
              This month: <span className="xyz-unit-text">{selectedItem.monthlyUsageQty}</span>
            </div>
          </div>

          <div className="xyz-metric-card">
            <div className="xyz-metric-header">
              <h4>Waste</h4>
            </div>
            <div className="xyz-metric-value" style={{color:'#000'}}>
              {selectedItem.wasteMonthValue || '₹0'}
            </div>
            <div className="xyz-metric-label">
              This month: <span className="xyz-unit-text">{selectedItem.wasteMonthQty}</span>
            </div>
          </div>
        </div>

        {/* Monthly Average Cost Line Chart */}
        {(() => {
          const rawUnit = (selectedItem.unitSymbol || '').toLowerCase().trim();
          const needsConversion = ['g','gram','grams','mg','milligram','milligrams','ml','milliliter','milliliters'].includes(rawUnit);
          const chartPriceUnit = getPriceDisplayUnit(selectedItem.unitSymbol);
          // Convert avg_cost data for display: multiply ×1000 for g/ml → kg/L
          const chartData = monthlyCostData.map(d => ({
            ...d,
            avg_cost_display: d.avg_cost != null ? (needsConversion ? d.avg_cost * 1000 : d.avg_cost) : null
          }));
          return (
            <div className="xyz-monthly-cost-chart">
              <h3 className="xyz-chart-title">Monthly Average Cost</h3>
              <p className="xyz-chart-subtitle">
                Weighted average purchase cost per {chartPriceUnit} (Jan – Dec {new Date().getFullYear()})
              </p>
              {monthlyCostLoading ? (
                <div className="xyz-chart-loading">
                  <div className="xyz-chart-spinner"></div>
                  <span>Loading chart data...</span>
                </div>
              ) : chartData.length > 0 ? (
                <div className="xyz-chart-wrapper">
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 16, left: -10, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickLine={false}
                        tickFormatter={(v) => v > 0 ? `₹${v}` : '0'}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '10px',
                          border: 'none',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          padding: '10px 14px',
                          fontSize: '13px'
                        }}
                        formatter={(value) => [
                          `₹${Number(value).toFixed(2)}`,
                          `Avg Cost/${chartPriceUnit}`
                        ]}
                        labelFormatter={(label) => {
                          const item = chartData.find(d => d.label === label);
                          return item ? item.full_label : label;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="avg_cost_display"
                        stroke="#ef6344"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: '#ef6344', stroke: '#fff', strokeWidth: 2 }}
                        activeDot={{ r: 6, fill: '#ef6344', stroke: '#fff', strokeWidth: 2 }}
                        connectNulls={true}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="xyz-chart-empty">
                  <span>No purchase data available for this year</span>
                </div>
              )}
            </div>
          );
        })()}

        <button
          className="xyz-advanced-card"
          onClick={() => {
            const leakageIngredientId = Number(selectedItem?.id ?? selectedItem?.item_id ?? selectedItem?.itemId ?? 0);
            goTo('leakage', {
              featureName: 'Advanced Features',
              backScreen: 'xyz',
              ingredientId: Number.isFinite(leakageIngredientId) && leakageIngredientId > 0 ? leakageIngredientId : undefined,
              ingredientName: selectedItem?.name
            });
          }}
        >
          <div className="xyz-advanced-left">
            <span className="xyz-advanced-icon">
              <FontAwesomeIcon icon={faChartLine} />
            </span>
            <span>
              <span className="xyz-advanced-title">Advanced Features</span>
            </span>
          </div>
          <span className="xyz-advanced-chevron">›</span>
        </button>
      </div>
    );
  }

  return null;
}
